import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Track impression when ad is viewed
const trackImpression = async (adId) => {
  try {
    await axios.post(`${API}/ads/${adId}/impression`);
  } catch (e) {
    // Silent fail
  }
};

// Track click
const trackClick = async (adId) => {
  try {
    await axios.post(`${API}/ads/${adId}/click`);
  } catch (e) {
    // Silent fail
  }
};

export function AdBanner({ position = "header", className = "", maxAds = 3 }) {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [rotationInterval, setRotationInterval] = useState(8000);

  // Fetch ads and rotation setting on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [adsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/ads?position=${position}`),
          axios.get(`${API}/settings/ad-rotation`).catch(() => ({ data: { interval: 8 } })),
        ]);
        if (adsRes.data.ads && adsRes.data.ads.length > 0) {
          const limitedAds = adsRes.data.ads.slice(0, maxAds);
          setAds(limitedAds);
          trackImpression(limitedAds[0].id);
        }
        setRotationInterval((settingsRes.data.interval || 8) * 1000);
      } catch (e) {
        // No ads available
      }
    };
    fetchData();
  }, [position, maxAds]);

  // Rotate ads
  const rotateAd = useCallback(() => {
    if (ads.length <= 1) return;
    
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentIndex((prev) => {
        const nextIndex = (prev + 1) % ads.length;
        // Track impression for new ad
        trackImpression(ads[nextIndex].id);
        return nextIndex;
      });
      setIsTransitioning(false);
    }, 300); // Transition duration
  }, [ads]);

  // Set up rotation interval
  useEffect(() => {
    if (ads.length <= 1) return;
    
    const interval = setInterval(rotateAd, rotationInterval);
    return () => clearInterval(interval);
  }, [ads.length, rotateAd, rotationInterval]);

  if (ads.length === 0) {
    // Reserve space for header ad to prevent CLS
    if (position === "header") return <div className={`w-full h-[100px] ${className}`} />;
    return null;
  }

  const currentAd = ads[currentIndex];

  const handleClick = () => {
    trackClick(currentAd.id);
  };

  // Dots indicator for multiple ads
  const DotsIndicator = () => {
    if (ads.length <= 1) return null;
    return (
      <div className="flex justify-center gap-1.5 mt-2">
        {ads.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsTransitioning(true);
              setTimeout(() => {
                setCurrentIndex(index);
                trackImpression(ads[index].id);
                setIsTransitioning(false);
              }, 300);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-slate-600 w-4' 
                : 'bg-slate-300 hover:bg-slate-400'
            }`}
            aria-label={`Visa annons ${index + 1}`}
          />
        ))}
      </div>
    );
  };

  if (position === "header") {
    return (
      <div className={`w-full bg-slate-100 ${className}`} data-testid="ad-banner-header">
        <a
          href={currentAd.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className="block max-w-7xl mx-auto"
        >
          <img
            src={currentAd.image_url}
            alt={currentAd.alt_text || "Annons"}
            width={1200}
            height={100}
            className={`w-full h-auto max-h-24 object-contain transition-opacity duration-300 ${
              isTransitioning ? 'opacity-0' : 'opacity-100'
            }`}
          />
        </a>
        <div className="flex items-center justify-center gap-2 pb-1">
          <p className="text-[10px] text-slate-400">Annons</p>
          {ads.length > 1 && (
            <span className="text-[10px] text-slate-300">
              {currentIndex + 1}/{ads.length}
            </span>
          )}
        </div>
        <DotsIndicator />
      </div>
    );
  }

  if (position === "sidebar") {
    return (
      <div className={`bg-slate-50 rounded-lg p-2 ${className}`} data-testid="ad-banner-sidebar">
        <a
          href={currentAd.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className="block"
        >
          <img
            src={currentAd.image_url}
            alt={currentAd.alt_text || "Annons"}
            className={`w-full h-auto rounded transition-opacity duration-300 ${
              isTransitioning ? 'opacity-0' : 'opacity-100'
            }`}
          />
        </a>
        <div className="flex items-center justify-center gap-2 mt-1">
          <p className="text-[10px] text-slate-400">Annons</p>
          {ads.length > 1 && (
            <span className="text-[10px] text-slate-300">
              {currentIndex + 1}/{ads.length}
            </span>
          )}
        </div>
        <DotsIndicator />
      </div>
    );
  }

  if (position === "table-row") {
    return (
      <div className={`bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-3 my-4 ${className}`} data-testid="ad-banner-table">
        <a
          href={currentAd.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className="flex items-center justify-center gap-4"
        >
          <img
            src={currentAd.image_url}
            alt={currentAd.alt_text || "Annons"}
            className={`h-16 w-auto object-contain transition-opacity duration-300 ${
              isTransitioning ? 'opacity-0' : 'opacity-100'
            }`}
          />
        </a>
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="text-[10px] text-slate-400">Annons</span>
          {ads.length > 1 && (
            <span className="text-[10px] text-slate-300">
              {currentIndex + 1}/{ads.length}
            </span>
          )}
        </div>
        <DotsIndicator />
      </div>
    );
  }

  return null;
}

export function AdPlaceholder({ position = "header", className = "" }) {
  // Show placeholder when no ads are booked - encourages advertisers
  const placeholderStyles = {
    header: "w-full h-20 bg-gradient-to-r from-slate-100 to-slate-200 flex items-center justify-center",
    sidebar: "w-full h-48 bg-gradient-to-b from-slate-100 to-slate-200 rounded-lg flex items-center justify-center",
    "table-row": "w-full h-16 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg flex items-center justify-center my-4",
  };

  return (
    <a
      href="/annonsera"
      className={`${placeholderStyles[position]} ${className} hover:opacity-80 transition-opacity`}
      data-testid={`ad-placeholder-${position}`}
    >
      <div className="text-center">
        <p className="text-sm font-medium text-slate-500">Din annons här?</p>
        <p className="text-xs text-slate-400">Kontakta oss för priser</p>
      </div>
    </a>
  );
}
